import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FaTachometerAlt,
  FaUserAlt,
  FaUserPlus,
  FaCogs,
  FaBoxOpen,
  FaUsers,
} from "react-icons/fa";
import { PiGridNineFill } from "react-icons/pi";
import { FiSettings } from "react-icons/fi";
import { RiCloseCircleLine } from "react-icons/ri";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBoxesStacked,
  faFileInvoiceDollar,
  faCartPlus,
  faEnvelope,
  faHandshake,
  faStore,
  faTasks,
  faHeadset,
  faChartLine,
  faUserTie,
  faFileInvoice,
  faUser,
  faMagnifyingGlass,
  faPerson,
} from "@fortawesome/free-solid-svg-icons";
import "../Style/MainSideBar.css";

export default function MainSidebar({ hamburger, sethamburger }) {
  const location = useLocation();

  // ── Persist sidebar open/closed state across page navigations ──
  // Each page mounts its own <MainSidebar>, so state must survive unmount.
  // sethamburger here is the SAME setState the parent page owns, so calling
  // it also updates the parent's own hamburger value used for its
  // content-wrapper margin/width — this keeps sidebar width and content
  // margin in sync across every page without editing each page file.
  useEffect(() => {
    const saved = localStorage.getItem("sidebarOpen");
    if (saved !== null) {
      const isOpen = saved === "true";
      if (isOpen !== hamburger) {
        sethamburger(isOpen);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    localStorage.setItem("sidebarOpen", String(hamburger));
  }, [hamburger]);

  // ── Hover-to-expand (collapsed state only) ──────────────────────
  // This is intentionally SEPARATE from `hamburger`. `hamburger` still
  // only changes on toggle click and still drives each page's content
  // margin — untouched. `isHoveringSidebar` only affects how THIS
  // component renders itself (an overlay flyout on top of the fixed
  // sidebar), so content never shifts when hover-expand kicks in.
  const [isHoveringSidebar, setIsHoveringSidebar] = useState(false);
  const hoverCloseTimeoutRef = useRef(null);
  const HOVER_CLOSE_DELAY_MS = 150; // debounce to prevent flicker at the edge

  const handleSidebarMouseEnter = () => {
    if (hoverCloseTimeoutRef.current) {
      clearTimeout(hoverCloseTimeoutRef.current);
      hoverCloseTimeoutRef.current = null;
    }
    setIsHoveringSidebar(true);
  };

  const handleSidebarMouseLeave = () => {
    if (hoverCloseTimeoutRef.current) {
      clearTimeout(hoverCloseTimeoutRef.current);
    }
    hoverCloseTimeoutRef.current = setTimeout(() => {
      setIsHoveringSidebar(false);
      hoverCloseTimeoutRef.current = null;
    }, HOVER_CLOSE_DELAY_MS);
  };

  useEffect(() => {
    return () => {
      if (hoverCloseTimeoutRef.current) {
        clearTimeout(hoverCloseTimeoutRef.current);
      }
    };
  }, []);

  // True when the sidebar should LOOK open — either because the user
  // manually toggled it open, or because they're currently hovering it
  // while it's collapsed. Manual open (hamburger === true) always wins
  // and is never auto-closed by mouse leave.
  const visuallyExpanded = hamburger || isHoveringSidebar;

  // ── Route detection ────────────────────────────────────────────
  const isEnteliSocialRedesignRoute = location.pathname.includes(
    "/crm-dashboard/enteli-social-redesign"
  );

  const [isShowSocialRedesign, setIsShowSocialRedesign] = useState(
    isEnteliSocialRedesignRoute
  );

  const handelShowSocialRedesign = () => {
    setIsShowSocialRedesign(!isShowSocialRedesign);
  };

  useEffect(() => {
    if (isEnteliSocialRedesignRoute) {
      setIsShowSocialRedesign(true);
    }
  }, [location.pathname, isEnteliSocialRedesignRoute]);

  const isVendorRoute =
    location.pathname.includes("/vendor-dashboard") ||
    location.pathname.includes("/all-vendors") ||
    location.pathname.includes("/new-vendor") ||
    location.pathname.includes("/vendor/products") ||
    location.pathname.includes("/vendor-details");

  useEffect(() => {
    if (isVendorRoute) {
      setIsShowVen(true);
    }
  }, [location.pathname, isVendorRoute]);

  const isCustomerRoute =
    location.pathname.includes("/customer-dashboard") ||
    location.pathname.includes("/all-customers") ||
    location.pathname.includes("/new-customer") ||
    location.pathname.includes("/customer-details") ||
    location.pathname.includes("/customers/");

  useEffect(() => {
    if (isCustomerRoute) {
      setIsComShow(true);
    }
  }, [location.pathname, isCustomerRoute]);

  const isLeadRoute =
    location.pathname.includes("/lead-dashboard") ||
    location.pathname.includes("/all-leads") ||
    location.pathname.includes("/new-lead") ||
    location.pathname.includes("/lead-details") ||
    location.pathname.includes("/leads/");

  useEffect(() => {
    if (isLeadRoute) {
      setIsShow(true);
    }
  }, [location.pathname, isLeadRoute]);

  const isInteractionRoute =
    location.pathname.includes("/interaction-dashboard") ||
    location.pathname.includes("/search-interaction");

  useEffect(() => {
    if (isInteractionRoute) {
      setIsShowInt(true);
    }
  }, [location.pathname, isInteractionRoute]);

  const isAccountingRoute = location.pathname.includes("/configure");

  useEffect(() => {
    if (isAccountingRoute) setIsConfig(true);
  }, [location.pathname, isAccountingRoute]);

  const isEmailRoute = location.pathname.includes("/send-bulk-email");

  useEffect(() => {
    if (isEmailRoute) setIsShowEmail(true);
  }, [location.pathname, isEmailRoute]);

  const isTaskRoute =
    location.pathname.includes("/task-dashboard") ||
    location.pathname.includes("/create-task");

  useEffect(() => {
    if (isTaskRoute) setIsShowTask(true);
  }, [location.pathname, isTaskRoute]);

  const isInventoryRoute =
    location.pathname.includes("/inventory-dashboard") ||
    location.pathname.includes("/product");

  useEffect(() => {
    if (isInventoryRoute) setIsShowStock(true);
  }, [location.pathname, isInventoryRoute]);

  const isSalesRoute =
    location.pathname.includes("/create-invoice") ||
    location.pathname.includes("/quotations");

  useEffect(() => {
    if (isSalesRoute) setIsShowSale(true);
  }, [location.pathname, isSalesRoute]);

  const isPurchaseRoute =
    location.pathname.includes("/expense-dashboard") ||
    location.pathname.includes("/new-expense");

  useEffect(() => {
    if (isPurchaseRoute) setIsShowPur(true);
  }, [location.pathname, isPurchaseRoute]);

  const isSettingRoute =
    location.pathname.includes("/user-profile") ||
    location.pathname.includes("/manage-user") ||
    location.pathname.includes("/add-employee") ||
    location.pathname.includes("/my-settings") ||
    location.pathname.includes("/company-settings") ||
    location.pathname.includes("/support-ticket") ||
    location.pathname.includes("/feature-request");

  useEffect(() => {
    if (isSettingRoute) setIsShowSet(true);
  }, [location.pathname, isSettingRoute]);

  // ── State declarations ──────────────────────────────────────
  const [isConfig, setIsConfig] = useState(false);
  const handelConfig = () => {
    setIsConfig(!isConfig);
  };

  const [isComShow, setIsComShow] = useState(false);
  const handelComShow = () => {
    setIsComShow(!isComShow);
  };

  const [isShow, setIsShow] = useState(false);
  const handelShow = () => {
    setIsShow(!isShow);
  };

  const [isShowVen, setIsShowVen] = useState(false);
  const handelShowVen = () => {
    setIsShowVen(!isShowVen);
  };

  const [isShowEmail, setIsShowEmail] = useState(false);
  const handelShowEmail = () => {
    setIsShowEmail(!isShowEmail);
  };

  const [isShowTask, setIsShowTask] = useState(false);
  const handelShowTask = () => {
    setIsShowTask(!isShowTask);
  };

  const [isShowInt, setIsShowInt] = useState(false);
  const handelShowInt = () => {
    setIsShowInt(!isShowInt);
  };

  const [isShowStock, setIsShowStock] = useState(false);
  const handelShowStock = () => {
    setIsShowStock(!isShowStock);
  };

  const [isShowSale, setIsShowSale] = useState(false);
  const handelShowSale = () => {
    setIsShowSale(!isShowSale);
  };

  const [isShowPur, setIsShowPur] = useState(false);
  const handelShowPur = () => {
    setIsShowPur(!isShowPur);
  };

  const [isShowSet, setIsShowSet] = useState(false);
  const handelShowSet = () => {
    setIsShowSet(!isShowSet);
  };

  // ── Style helpers ────────────────────────────────────────────
  // Keyed off `visuallyExpanded` (not raw `hamburger`) so labels also
  // appear during hover-expand, matching the manually-open look.
  const getNavHeadStyle = () => {
    return !visuallyExpanded ? { display: "none" } : {};
  };

  const getNavIconStyle = () => {
    return !visuallyExpanded ? { fontSize: "1.2rem" } : {};
  };

  // ── Dropdown handlers ──────────────────────────────────────
  const handleDropdown = (dropdownState, setDropdownState) => {
    return (e) => {
      e.preventDefault();
      setIsConfig(false);
      setIsComShow(false);
      setIsShow(false);
      setIsShowVen(false);
      setIsShowTask(false);
      setIsShowEmail(false);
      setIsShowInt(false);
      setIsShowStock(false);
      setIsShowSocialRedesign(false);
      setIsShowSale(false);
      setIsShowPur(false);
      setIsShowSet(false);

      setDropdownState(!dropdownState);
    };
  };

  const handleDropdownAndNavigate = (dropdownState, setDropdownState) => {
    return () => {
      setIsConfig(false);
      setIsComShow(false);
      setIsShow(false);
      setIsShowVen(false);
      setIsShowTask(false);
      setIsShowEmail(false);
      setIsShowInt(false);
      setIsShowStock(false);
      setIsShowSocialRedesign(false);
      setIsShowSale(false);
      setIsShowPur(false);
      setIsShowSet(false);

      setDropdownState(!dropdownState);
    };
  };

  const handleSignOut = () => {
    localStorage.removeItem("accessToken");
    window.location.href = "/login";
  };

  return (
    <>
      {hamburger && (
        <div
          className="sidebar-backdrop d-lg-none"
          onClick={() => sethamburger(false)}
          aria-hidden="true"
        />
      )}

      <div
        className={`main-sidebar${!visuallyExpanded ? " collapsed sidebar-collapsed" : ""
          }`}
        onMouseEnter={handleSidebarMouseEnter}
        onMouseLeave={handleSidebarMouseLeave}
      >
        <div className="sidebar-container">
          {/* ── Brand / Dashboard ── */}
          <div
            className="py-2 sidebar"
            style={{ borderBottom: "1px solid #4b545c" }}
          >
            <Link to="/crm-dashboard" className="brand-link">
              <div className="nav-icon fs-3">
                <PiGridNineFill />
              </div>
              <div className="navhead fs-5 ff" style={getNavHeadStyle()}>
                <p>Dashboard</p>
              </div>
            </Link>
          </div>

          {/* ── Marketing ── */}
          <div
            className="mt-3 mb-2 pb-2 sidebar"
            style={{ borderBottom: "1px solid #4b545c" }}
          >
            <Link to="#" className="brand-link fs-6">
              <div className="nav-icon">
                <i className="fa fa-cubes fs-5" aria-hidden="true"></i>
              </div>
              <div className="navhead ff" style={getNavHeadStyle()}>
                <p>Marketing</p>
              </div>
            </Link>
          </div>

          {/* ── Navigation ── */}
          <nav className="mt-2 sidebar">
            <ul
              className="nav nav-pills nav-sidebar flex-column"
              data-widget="treeview"
              role="menu"
              data-accordion="false"
            >
              {/* ── Accounting ── */}
              <li className="nav-item">
                <a
                  href="#"
                  className="nav-link"
                  onClick={handleDropdown(isConfig, handelConfig)}
                >
                  <FontAwesomeIcon
                    icon={faFileInvoice}
                    className="nav-icon"
                    style={getNavIconStyle()}
                  />
                  <div className="navhead" style={getNavHeadStyle()}>
                    <p>Accounting</p>
                    <i
                      className={`${isConfig
                        ? "fa fa-angle-down right"
                        : "fa fa-angle-left right"
                        }`}
                      style={{ cursor: "pointer" }}
                    />
                  </div>
                </a>
                <ul className="sub-menu">
                  <li
                    className={`nav-item ${isConfig ? "menu-open" : "d-none"}`}
                  >
                    <Link to="/configure" className="nav-link-sub">
                      <img
                        src="/assets/configuration.png"
                        alt="configuration"
                        className="nav-icon"
                      />
                      <p>Journal Configuration</p>
                    </Link>
                  </li>
                </ul>
              </li>

              {/* ── Customer ── */}
              <li className="nav-item">
                <Link
                  to="/customer-dashboard"
                  className="nav-link"
                  onClick={handleDropdownAndNavigate(isComShow, handelComShow)}
                >
                  <FaUsers className="nav-icon" style={getNavIconStyle()} />
                  <div className="navhead">
                    <p>Customer</p>
                    <i
                      className={`${isComShow
                        ? "fa fa-angle-down right"
                        : "fa fa-angle-left right"
                        }`}
                      style={{ cursor: "pointer" }}
                    />
                  </div>
                </Link>
                <ul className="sub-menu">
                  <li
                    className={`nav-item ${isComShow ? "menu-open" : "d-none"}`}
                  >
                    <Link to="/customer-dashboard" className="nav-link-sub">
                      <img
                        src="/assets/dashboard.png"
                        alt="dashboard"
                        className="nav-icon"
                      />
                      <p>Customer Dashboard</p>
                    </Link>
                  </li>
                  <li
                    className={`nav-item ${isComShow ? "menu-open" : "d-none"}`}
                  >
                    <Link to="/all-customers" className="nav-link-sub">
                      <FaUsers className="nav-icon" />
                      <p>All Customers</p>
                    </Link>
                  </li>
                  <li
                    className={`nav-item ${isComShow ? "menu-open" : "d-none"}`}
                  >
                    <Link to="/new-customer" className="nav-link-sub">
                      <FaUserPlus className="nav-icon" />
                      <p>New Customer</p>
                    </Link>
                  </li>
                </ul>
              </li>

              {/* ── Lead ── */}
              <li className="nav-item">
                <Link
                  to="/lead-dashboard"
                  className="nav-link"
                  onClick={handleDropdownAndNavigate(isShow, handelShow)}
                >
                  <FontAwesomeIcon
                    icon={faUserTie}
                    className="nav-icon"
                    style={getNavIconStyle()}
                  />
                  <div className="navhead">
                    <p>Lead</p>
                    <i
                      className={`${isShow
                        ? "fa fa-angle-down right"
                        : "fa fa-angle-left right"
                        }`}
                      style={{ cursor: "pointer" }}
                    />
                  </div>
                </Link>
                <ul className="sub-menu">
                  <li className={`nav-item ${isShow ? "menu-open" : "d-none"}`}>
                    <Link to="/lead-dashboard" className="nav-link-sub">
                      <FaTachometerAlt className="nav-icon" />
                      <p>Lead Dashboard</p>
                    </Link>
                  </li>
                  <li className={`nav-item ${isShow ? "menu-open" : "d-none"}`}>
                    <Link to="/all-leads" className="nav-link-sub">
                      <FaUsers className="nav-icon" />
                      <p>All Leads</p>
                    </Link>
                  </li>
                  <li className={`nav-item ${isShow ? "menu-open" : "d-none"}`}>
                    <Link to="/new-lead" className="nav-link-sub">
                      <FaUserPlus className="nav-icon" />
                      <p>New Lead</p>
                    </Link>
                  </li>
                </ul>
              </li>

              {/* ── Vendor ── */}
              <li className="nav-item">
                <Link
                  to="/vendor-dashboard"
                  className="nav-link"
                  onClick={handleDropdownAndNavigate(isShowVen, handelShowVen)}
                >
                  <FontAwesomeIcon
                    icon={faStore}
                    className="nav-icon"
                    style={getNavIconStyle()}
                  />
                  <div className="navhead" style={getNavHeadStyle()}>
                    <p>Vendor</p>
                    <i
                      className={`${isShowVen
                        ? "fa fa-angle-down right"
                        : "fa fa-angle-left right"
                        }`}
                      style={{ cursor: "pointer" }}
                    />
                  </div>
                </Link>
                <ul className="sub-menu">
                  <li
                    className={`nav-item ${isShowVen ? "menu-open" : "d-none"}`}
                  >
                    <Link to="/vendor-dashboard" className="nav-link-sub">
                      <img
                        src="/assets/dashboard.png"
                        alt="dashboard"
                        className="nav-icon"
                      />
                      <p>Vendor Dashboard</p>
                    </Link>
                  </li>
                  <li
                    className={`nav-item ${isShowVen ? "menu-open" : "d-none"}`}
                  >
                    <Link to="/all-vendors" className="nav-link-sub">
                      <FaUsers className="nav-icon" />
                      <p>All Vendors</p>
                    </Link>
                  </li>
                  <li
                    className={`nav-item ${isShowVen ? "menu-open" : "d-none"}`}
                  >
                    <Link to="/new-vendor" className="nav-link-sub">
                      <FaUserPlus className="nav-icon" />
                      <p>New Vendor</p>
                    </Link>
                  </li>
                  <li
                    className={`nav-item ${isShowVen ? "menu-open" : "d-none"}`}
                  >
                    <Link to="/vendor/products" className="nav-link-sub">
                      <FaBoxOpen className="nav-icon" />
                      <p>Products</p>
                    </Link>
                  </li>
                </ul>
              </li>

              {/* ── Email ── */}
              <li className="nav-item">
                <a
                  href="#"
                  className="nav-link"
                  onClick={handleDropdown(isShowEmail, handelShowEmail)}
                >
                  <FontAwesomeIcon
                    icon={faEnvelope}
                    className="nav-icon"
                    style={getNavIconStyle()}
                  />
                  <div className="navhead" style={getNavHeadStyle()}>
                    <p>Email</p>
                    <div>
                      <span className="badge badge-info right mr-1">6</span>
                      <i
                        className={`${isShowEmail
                          ? "fa fa-angle-down right"
                          : "fa fa-angle-left right"
                          }`}
                        style={{ cursor: "pointer" }}
                      />
                    </div>
                  </div>
                </a>
                <ul className="sub-menu">
                  <li
                    className={`nav-item ${isShowEmail ? "menu-open" : "d-none"
                      }`}
                  >
                    <Link to="/send-bulk-email" className="nav-link-sub">
                      <img
                        src="/assets/emails.png"
                        alt="bulk-email"
                        className="nav-icon"
                      />
                      <p>Send Bulk Email</p>
                    </Link>
                  </li>
                  <li
                    className={`nav-item ${isShowEmail ? "menu-open" : "d-none"
                      }`}
                  >
                    <Link to="#" className="nav-link-sub">
                      <img
                        src="/assets/send_email.png"
                        alt="send email"
                        className="nav-icon"
                      />
                      <p>Send Email</p>
                    </Link>
                  </li>
                </ul>
              </li>

              {/* ── Task ── */}
              <li className="nav-item">
                <Link
                  to="/task-dashboard"
                  className="nav-link"
                  onClick={handleDropdownAndNavigate(isShowTask, handelShowTask)}
                >
                  <FontAwesomeIcon
                    icon={faTasks}
                    className="nav-icon"
                    style={getNavIconStyle()}
                  />
                  <div className="navhead">
                    <p>Task</p>
                    <i
                      className={`${isShowTask
                        ? "fa fa-angle-down right"
                        : "fa fa-angle-left right"
                        }`}
                      style={{ cursor: "pointer" }}
                    />
                  </div>
                </Link>
                <ul className="sub-menu">
                  <li
                    className={`nav-item ${isShowTask ? "menu-open" : "d-none"
                      }`}
                  >
                    <Link to="/task-dashboard" className="nav-link-sub">
                      <img
                        src="/assets/dashboard.png"
                        alt="dashboard"
                        className="nav-icon"
                      />
                      <p>Task Dashboard</p>
                    </Link>
                  </li>
                  <li
                    className={`nav-item ${isShowTask ? "menu-open" : "d-none"
                      }`}
                  >
                    <Link to="/create-task" className="nav-link-sub">
                      <FaUserPlus className="nav-icon" />
                      <p>Create Task</p>
                    </Link>
                  </li>
                </ul>
              </li>

              {/* ── Interaction ── */}
              <li className="nav-item">
                <a
                  href="#"
                  className="nav-link"
                  onClick={handleDropdown(isShowInt, handelShowInt)}
                >
                  <FontAwesomeIcon
                    icon={faHandshake}
                    className="nav-icon"
                    style={getNavIconStyle()}
                  />
                  <div className="navhead">
                    <p>Interaction</p>
                    <div>
                      <span className="badge badge-info right mr-1">6</span>
                      <i
                        className={`${isShowInt
                          ? "fa fa-angle-down right"
                          : "fa fa-angle-left right"
                          }`}
                        style={{ cursor: "pointer" }}
                      />
                    </div>
                  </div>
                </a>
                <ul className="sub-menu">
                  <li
                    className={`nav-item ${isShowInt ? "menu-open" : "d-none"}`}
                  >
                    <Link to="/interaction-dashboard" className="nav-link-sub">
                      <img
                        src="/assets/dashboard.png"
                        alt="dashboard"
                        className="nav-icon"
                      />
                      <p>Interaction Dashboard</p>
                    </Link>
                  </li>
                  <li
                    className={`nav-item ${isShowInt ? "menu-open" : "d-none"}`}
                  >
                    <Link to="/search-interaction" className="nav-link-sub">
                      <FontAwesomeIcon
                        icon={faMagnifyingGlass}
                        className="nav-icon"
                      />
                      <p>Search Interaction</p>
                    </Link>
                  </li>
                </ul>
              </li>

              {/* ── Service Request ── */}
              <li className="nav-item">
                <Link to="#" className="nav-link">
                  <FontAwesomeIcon
                    icon={faHeadset}
                    className="nav-icon"
                    style={getNavIconStyle()}
                  />
                  <div className="navhead">
                    <p>Service Request</p>
                    <span className="badge badge-info right">6</span>
                  </div>
                </Link>
              </li>

              {/* ── Inventory ── */}
              <li className="nav-item">
                <a
                  href="#"
                  className="nav-link"
                  onClick={handleDropdown(isShowStock, handelShowStock)}
                >
                  <FontAwesomeIcon
                    icon={faBoxesStacked}
                    className="nav-icon"
                    style={getNavIconStyle()}
                  />
                  <div className="navhead">
                    <p>Inventory</p>
                    <div>
                      <span className="badge badge-info right mr-1">6</span>
                      <i
                        className={`${isShowStock
                          ? "fa fa-angle-down right"
                          : "fa fa-angle-left right"
                          }`}
                        style={{ cursor: "pointer" }}
                      />
                    </div>
                  </div>
                </a>
                <ul className="sub-menu">
                  <li
                    className={`nav-item ${isShowStock ? "menu-open" : "d-none"
                      }`}
                  >
                    <Link to="/inventory-dashboard" className="nav-link-sub">
                      <FontAwesomeIcon icon={faBoxesStacked} className="nav-icon" />
                      <p>Inventory Dashboard</p>
                    </Link>
                  </li>
                  <li
                    className={`nav-item ${isShowStock ? "menu-open" : "d-none"
                      }`}
                  >
                    <Link to="/inventory-dashboard" className="nav-link-sub">
                      <img
                        src="/assets/stock.png"
                        alt="stock"
                        className="nav-icon"
                        style={getNavIconStyle()}
                      />
                      <p>View Stock</p>
                    </Link>
                  </li>
                  <li
                    className={`nav-item ${isShowStock ? "menu-open" : "d-none"
                      }`}
                  >
                    <Link to="/product" className="nav-link-sub">
                      <FontAwesomeIcon icon={faCartPlus} className="nav-icon" />
                      <p>Add Product</p>
                    </Link>
                  </li>
                </ul>
              </li>

              {/* ── Sales ── */}
              <li className="nav-item">
                <a
                  href="#"
                  className="nav-link"
                  onClick={handleDropdown(isShowSale, handelShowSale)}
                >
                  <FontAwesomeIcon
                    icon={faChartLine}
                    className="nav-icon"
                    style={getNavIconStyle()}
                  />
                  <div className="navhead">
                    <p>Sales</p>
                    <div>
                      <span className="badge badge-info right mr-1">6</span>
                      <i
                        className={`${isShowSale
                          ? "fa fa-angle-down right"
                          : "fa fa-angle-left right"
                          }`}
                        style={{ cursor: "pointer" }}
                      />
                    </div>
                  </div>
                </a>
                <ul className="sub-menu">
                  <li
                    className={`nav-item ${isShowSale ? "menu-open" : "d-none"
                      }`}
                  >
                    <Link to="#" className="nav-link-sub">
                      <img
                        src="/assets/dashboard.png"
                        alt="dashboard"
                        className="nav-icon"
                      />
                      <p>Invoice Dashboard</p>
                    </Link>
                  </li>
                  <li
                    className={`nav-item ${isShowSale ? "menu-open" : "d-none"
                      }`}
                  >
                    <Link to="/create-invoice" className="nav-link-sub">
                      <img
                        src="/assets/invoice.png"
                        alt="invoice"
                        className="nav-icon"
                      />
                      <p>Create New Invoice</p>
                    </Link>
                  </li>
                  <li
                    className={`nav-item ${isShowSale ? "menu-open" : "d-none"
                      }`}
                  >
                    <Link to="#" className="nav-link-sub">
                      <img
                        src="/assets/dashboard.png"
                        alt="dashboard"
                        className="nav-icon"
                      />
                      <p>Purchase Order Dashboard</p>
                    </Link>
                  </li>
                  <li
                    className={`nav-item ${isShowSale ? "menu-open" : "d-none"
                      }`}
                  >
                    <Link to="#" className="nav-link-sub">
                      <img
                        src="/assets/purchase.png"
                        alt="purchase"
                        className="nav-icon"
                      />
                      <p>Create New Purchase Order</p>
                    </Link>
                  </li>
                  <li
                    className={`nav-item ${isShowSale ? "menu-open" : "d-none"
                      }`}
                  >
                    <Link to="#" className="nav-link-sub">
                      <img
                        src="/assets/dashboard.png"
                        alt="dashboard"
                        className="nav-icon"
                      />
                      <p>Proforma Dashboard</p>
                    </Link>
                  </li>
                  <li
                    className={`nav-item ${isShowSale ? "menu-open" : "d-none"
                      }`}
                  >
                    <Link to="#" className="nav-link-sub">
                      <img
                        src="/assets/proforma.png"
                        alt="proforma"
                        className="nav-icon"
                      />
                      <p>Create New Proforma Invoice</p>
                    </Link>
                  </li>
                  <li
                    className={`nav-item ${isShowSale ? "menu-open" : "d-none"
                      }`}
                  >
                    <Link to="/quotations" className="nav-link-sub">
                      <img
                        src="/assets/dashboard.png"
                        alt="dashboard"
                        className="nav-icon"
                      />
                      <p>Quotation Dashboard</p>
                    </Link>
                  </li>
                  <li
                    className={`nav-item ${isShowSale ? "menu-open" : "d-none"
                      }`}
                  >
                    <Link to="/quotations" className="nav-link-sub">
                      <img
                        src="/assets/quotation.png"
                        alt="quotation"
                        className="nav-icon"
                      />
                      <p>Quotations List</p>
                    </Link>
                  </li>
                </ul>
              </li>

              {/* ── Purchase ── */}
              <li className="nav-item">
                <a
                  href="#"
                  className="nav-link"
                  onClick={handleDropdown(isShowPur, handelShowPur)}
                >
                  <FontAwesomeIcon
                    icon={faCartPlus}
                    className="nav-icon"
                    style={getNavIconStyle()}
                  />
                  <div className="navhead">
                    <p>Purchase</p>
                    <div>
                      <span className="badge badge-info right mr-1">6</span>
                      <i
                        className={`${isShowPur
                          ? "fa fa-angle-down right"
                          : "fa fa-angle-left right"
                          }`}
                        style={{ cursor: "pointer" }}
                      />
                    </div>
                  </div>
                </a>
                <ul className="sub-menu">
                  <li
                    className={`nav-item ${isShowPur ? "menu-open" : "d-none"}`}
                  >
                    <Link to="/expense-dashboard" className="nav-link-sub">
                      <img
                        src="/assets/dashboard.png"
                        alt="dashboard"
                        className="nav-icon"
                      />
                      <p>Expense Dashboard</p>
                    </Link>
                  </li>
                  <li
                    className={`nav-item ${isShowPur ? "menu-open" : "d-none"}`}
                  >
                    <Link to="/new-expense" className="nav-link-sub">
                      <FontAwesomeIcon
                        icon={faFileInvoiceDollar}
                        className="nav-icon"
                      />
                      <p>New Expense</p>
                    </Link>
                  </li>
                </ul>
              </li>

              {/* ── Setting ── */}
              <li className="nav-item ml-1">
                <a
                  href="#"
                  className="nav-link"
                  onClick={handleDropdown(isShowSet, handelShowSet)}
                >
                  <FiSettings className="nav-icon" style={getNavIconStyle()} />
                  <div className="navhead">
                    <p>Setting</p>
                    <i
                      className={`${isShowSet
                        ? "fa fa-angle-down right"
                        : "fa fa-angle-left right"
                        }`}
                      style={{ cursor: "pointer" }}
                    />
                  </div>
                </a>
                <ul className="sub-menu">
                  <li
                    className={`nav-item ${isShowSet ? "menu-open" : "d-none"}`}
                  >
                    <Link to="/user-profile" className="nav-link-sub">
                      <FaUserAlt className="nav-icon" style={getNavIconStyle()} />
                      <p>User Profile</p>
                    </Link>
                  </li>
                  <li
                    className={`nav-item ${isShowSet ? "menu-open" : "d-none"}`}
                  >
                    <Link to="/manage-user" className="nav-link-sub">
                      <FaUserPlus className="nav-icon" />
                      <p>Manage User</p>
                    </Link>
                  </li>
                  <li
                    className={`nav-item ${isShowSet ? "menu-open" : "d-none"}`}
                  >
                    <Link to="#" className="nav-link-sub">
                      <i
                        className="fa fa-file-text nav-icon"
                        aria-hidden="true"
                        style={getNavIconStyle()}
                      />
                      <p>Invoice Setting</p>
                    </Link>
                  </li>
                  <li
                    className={`nav-item ${isShowSet ? "menu-open" : "d-none"}`}
                  >
                    <Link to="#" className="nav-link-sub">
                      <FaTachometerAlt className="nav-icon" />
                      <p>HRM</p>
                    </Link>
                  </li>
                  <li
                    className={`nav-item ${isShowSet ? "menu-open" : "d-none"}`}
                  >
                    <Link to="/add-employee" className="nav-link-sub">
                      <FaUserPlus className="nav-icon" />
                      <p>Add Employee</p>
                    </Link>
                  </li>
                  <li
                    className={`nav-item ${isShowSet ? "menu-open" : "d-none"}`}
                  >
                    <Link to="/my-settings" className="nav-link-sub">
                      <FiSettings className="nav-icon" />
                      <p>My Settings</p>
                    </Link>
                  </li>
                  <li
                    className={`nav-item ${isShowSet ? "menu-open" : "d-none"}`}
                  >
                    <Link to="/company-settings" className="nav-link-sub">
                      <i className="fa-solid fa-briefcase nav-icon" />
                      <p>Company Settings</p>
                    </Link>
                  </li>
                  <li
                    className={`nav-item ${isShowSet ? "menu-open" : "d-none"}`}
                  >
                    <Link to="/support-ticket" className="nav-link-sub">
                      <i className="fa-solid fa-ticket nav-icon" />
                      <p>Support tickets</p>
                    </Link>
                  </li>
                  <li
                    className={`nav-item ${isShowSet ? "menu-open" : "d-none"}`}
                  >
                    <Link to="/feature-request" className="nav-link-sub">
                      <FontAwesomeIcon
                        className="nav-icon"
                        icon={faPerson}
                        style={getNavIconStyle()}
                      />
                      <p>Feature Request</p>
                    </Link>
                  </li>
                </ul>
              </li>

              {/* ── Sign Out ── */}
              <li className="nav-item">
                <Link to="/login" className="nav-link" onClick={handleSignOut}>
                  <RiCloseCircleLine className="nav-icon" style={getNavIconStyle()} />
                  <div className="navhead">
                    <p className="w-100">Sign Out</p>
                  </div>
                </Link>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </>
  );
}